import React, { useState } from 'react';
import { PageName, Language } from '../../types/pageBuilder';
import PageEditor from '../../components/admin/PageEditor/PageEditor';

/**
 * Main Page Builder page
 * WordPress-like visual page editor for Home, About, and Contact pages
 */
const PageBuilder: React.FC = () => {
  const [selectedPage, setSelectedPage] = useState<PageName>('home');
  const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');
  const [lastSaved] = useState<Date | null>(null);

  const pages: { name: PageName; display: string; icon: string }[] = [
    { name: 'home', display: 'Home', icon: '🏠' },
    { name: 'about', display: 'About', icon: 'ℹ️' },
    { name: 'contact', display: 'Contact', icon: '📧' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Page Builder</h1>
              <p className="text-sm text-gray-600 mt-1">
                Visual editor for managing page sections and content
              </p>
            </div>

            <div className="flex items-center space-x-4">
              {/* Last Saved Indicator */}
              {lastSaved && (
                <div className="text-sm text-gray-500">
                  ⏱️ Last saved: {lastSaved.toLocaleTimeString()}
                </div>
              )}

              {/* Language Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setSelectedLanguage('en')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedLanguage === 'en'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  🇬🇧 English
                </button>
                <button
                  onClick={() => setSelectedLanguage('ar')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    selectedLanguage === 'ar'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  🇸🇦 Arabic
                </button>
              </div>
            </div>
          </div>

          {/* Page Tabs */}
          <div className="flex space-x-2 mt-4 border-b border-gray-200">
            {pages.map((page) => (
              <button
                key={page.name}
                onClick={() => setSelectedPage(page.name)}
                className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
                  selectedPage === page.name
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{page.icon}</span>
                {page.display}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Page Editor Content */}
      <div className="max-w-7xl mx-auto">
        <PageEditor key={`${selectedPage}-${selectedLanguage}`} pageName={selectedPage} language={selectedLanguage} />
      </div>

      {/* Info Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <div className="text-gray-600">
            <strong>Current View:</strong> {pages.find((p) => p.name === selectedPage)?.display} Page
            ({selectedLanguage.toUpperCase()})
          </div>
          <div className="flex items-center space-x-4 text-gray-500">
            <span>💾 Auto-save enabled</span>
            <span>•</span>
            <span>🔒 Admin access only</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageBuilder;
